import { useTranslation } from "next-i18next";
import { ScrollView } from "../UI/ScrollView";
import Image from "next/legacy/image";
import { formatThousand } from "utils";
import UncheckedIcon from "../../public/icon/unchecked2.svg";
import CheckedIcon from "../../public/icon/checked2.svg";
import Button from "../UI/Button";
import classnames from "classnames";
import { ContractInfo } from "./contract-info";
import { Confirmed } from "./confirmed";
import { translate } from "utils/i18n";
import { useSnackbar } from "notistack";
import { useCopyToClipboard } from "react-use";
import { useState } from "react";
import { goodExchangeApi } from "utils/api";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { LottiePlayer } from "../common/lottie-player";
import { ItemBox } from "./item-box";
import { debounce } from "@mui/material";
import { BlueLink } from "../BlueLink";
import { SubGoodsTypeEnum } from "@/constants/mall";
import { SuccessContent } from "./success-content";

const Confirm = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  onConfirmRedeem,
  title,
  windowHeight,
}) => {
  const { t } = useTranslation("mall");

  return (
    <div className="bg-white pb-[40px]">
      <div className="h-[80px] flex items-center justify-center font-DIN_Alternate font-bold text-[24px] border-0 border-b border-solid border-black">
        {t("orderConfirmTitle")}
      </div>

      <div className="w-[400px] mx-auto mt-[24px]">
        <ItemBox
          img={item.mainPic}
          title={title}
          postage={0}
          cupPoints={item.cupPoints}
        />
        <div className="h-[1px] bg-[#E3E5E7] scale-y-50 my-[24px]"></div>

        <div className="relative">
          <ScrollView
            className="overflow-auto pr-[24px] w-[424px] pb-[24px]"
            style={{ maxHeight: Math.min(442, windowHeight - 400) }}
          >
            <div>
              <ContractInfo
                formValues={formValues}
                formErrors={formErrors}
                onFieldChange={onFieldChange}
                shouldValidateDiscordId
              />

              <div className="flex items-center">
                <button
                  className="btn-unstyled h-[16px]"
                  onClick={() => {
                    setShouldSave(!shouldSave);
                  }}
                >
                  {shouldSave ? (
                    <CheckedIcon className="w-[16px]" />
                  ) : (
                    <UncheckedIcon className="w-[16px]" />
                  )}
                </button>
                <span className="ml-[8px] text-[14px] font-medium transition">
                  {t("saveTips")}
                </span>
              </div>
            </div>
          </ScrollView>
          <div className="absolute bottom-[-2px] w-full h-[24px] bg-gradient-to-t from-white to-[rgba(255,255,255,0.0001)]"></div>
        </div>

        <div className="mt-[24px]">
          <Button
            variant="contained"
            color="secondary"
            component="span"
            className={classnames(
              "w-full h-full rounded-full transition font-black text-[18px]",
              "text-white hover:opacity-80"
            )}
            onClick={debounce(onConfirmRedeem, 300)}
          >
            <span>{t("btnConfirm")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

const Success = ({ item, goodExchangeData, title, windowHeight }) => {
  const { t } = useTranslation("mall");
  const { t: commonT } = useTranslation("common");
  const { enqueueSnackbar } = useSnackbar();
  const [state, copyToClipboard] = useCopyToClipboard();

  const code = goodExchangeData?.goodsNumber;

  const copy = () => {
    copyToClipboard(code);
    enqueueSnackbar(commonT("copySuccess"), { variant: "success" });
  };

  let content;

  if (item.subGoodsType === SubGoodsTypeEnum.SPRING_GIFT_BAG) {
    content = (
      <div className="mt-[4px] rounded-[40px] overflow-hidden py-[38px] bg-white">
        <div className="w-[400px] mx-auto">
          <div className="text-[20px] font-bold mb-[16px]">{title}</div>
          <div className="h-px bg-[#E3E5E7] scale-y-50 mb-[20px]"></div>

          <div className="font-medium mb-[12px]">{t("howToRedeem")}</div>

          <SuccessContent />
        </div>
      </div>
    );
  } else if (code) {
    content = (
      <div className="mt-[4px] rounded-[40px] overflow-hidden py-[38px] bg-white">
        <div className="w-[400px] mx-auto">
          <div className="text-[20px] font-bold mb-[16px]">{title}</div>
          <div className="h-px bg-[#E3E5E7] scale-y-50 mb-[20px]"></div>

          <div className="font-medium mb-[12px]">{t("activationTitle")}</div>

          <div className="h-[56px] rounded-full border border-solid border-black bg-[#F5F7FA] flex items-center justify-center mb-[8px] text-[#FF2800]">
            {code}
          </div>

          <Button
            variant="contained"
            color="secondary"
            component="span"
            className={classnames(
              "w-full h-[56px] rounded-full transition font-black text-[18px]",
              "text-white hover:opacity-80"
            )}
            onClick={copy}
          >
            <span>{t("copyBtn")}</span>
          </Button>

          {+goodExchangeData?.goodsTime >= 0 && (
            <div className="whitespace-pre-wrap text-[#686868] font-light text-[12px] leading-[20px] mt-[16px]">
              {translate(t("activationTips"), {
                time: dayjs(goodExchangeData?.goodsTime * 1000).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ScrollView
      className="overflow-auto relative"
      style={{ maxHeight: windowHeight - 32 }}
    >
      <LottiePlayer />

      <Confirmed
        extra={
          item.goodsLink && (
            <div className="mt-[8px] text-[14px]">
              {t("linkLabel")}
              <BlueLink href={item.goodsLink}>{item.goodsLink}</BlueLink>
            </div>
          )
        }
      />

      {content}
    </ScrollView>
  );
};

export const ItemModalOrderVipcard = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  validateField,
  title,
  windowHeight,
}) => {
  const [step, setStep] = useState(1);
  const [goodExchangeData, setGoodExchangeData] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirmRedeem = async () => {
    if (!validateField("email", formValues?.email)) {
      return;
    }

    if (!validateField("discordId", formValues?.discordId)) {
      return;
    }

    const goodExchangeApiRes = await goodExchangeApi({
      goodsId: item.goodsId,
      contact: {
        emailAddress: formValues.email,
        twitterHandle: formValues.twitterHandle,
        phoneNumber: formValues.phone,
      },
      discordId: formValues.discordId,
      isRemembered: shouldSave,
    });
    console.log("goodExchangeApiRes", { goodExchangeApiRes });

    if (goodExchangeApiRes.err) {
      enqueueSnackbar(goodExchangeApiRes?.err?.response?.data?.message, {
        variant: "error",
      });
      return;
    }

    setGoodExchangeData(goodExchangeApiRes.data);

    setStep(2);
  };

  return (
    <>
      {step === 1 && (
        <Confirm
          item={item}
          formValues={formValues}
          formErrors={formErrors}
          onFieldChange={onFieldChange}
          shouldSave={shouldSave}
          setShouldSave={setShouldSave}
          onConfirmRedeem={handleConfirmRedeem}
          title={title}
          windowHeight={windowHeight}
        />
      )}

      {step === 2 && (
        <Success
          item={item}
          goodExchangeData={goodExchangeData}
          title={title}
          windowHeight={windowHeight}
        />
      )}
    </>
  );
};
